/**
 * AlertMoreDetails
 * The collapsible "View more details" section at the bottom of the panel.
 * Contains the Activity Timeline (with proper no-data state) and Partner/Client
 * status cards, shown side by side in a 2-column grid.
 */
import React, { useState } from 'react';
import { FiActivity, FiAlertCircle, FiServer, FiMonitor } from 'react-icons/fi';
import _ from 'lodash';
import type {
  ActivityLogEntry,
  ClientStatus,
  IAlertProps,
  PartnerStatus,
} from '@/pages/Alert/alerts.interface';
import {
  CardTitles,
  NoDataMessages,
  SectionTitles,
} from '@/services/alert/alerts.constants';
import { AlertStepActionStatus } from '@/constant/alerts/alerts.constant';
import { ORGANISM_PROPS } from '@/constant/global/global.constant';
import { NoDataPlate } from '@/components/organism/TableExpand/primitives';
import { SectionCard } from '@/components/organism/Cards/SectionCard';
import { DisclosureButton } from '@/components/elements/DisclosureButton';
import { AlertTimelineItem } from './AlertTimelineItem';
import { AlertStatusCard } from './AlertStatusCard';

interface AlertMoreDetailsProps {
  isActivityLogDataReceived: boolean;
  activityLog?: ActivityLogEntry[];
  isHavingClientFlow: boolean;
  isHavingPartnerFlow: boolean;
  partner?: PartnerStatus | null;
  client?: ClientStatus | null;
  eachStepSummary: Record<string, AlertStepActionStatus>;
  toggleTestId?: string;
  contentTestId?: string;
  timelineTestId?: string;
}

export const AlertMoreDetails = ({
  isActivityLogDataReceived,
  activityLog,
  isHavingClientFlow,
  isHavingPartnerFlow,
  partner,
  client,
  eachStepSummary,
  toggleTestId,
  contentTestId,
  timelineTestId,
}: AlertMoreDetailsProps) => {
  const [open, setOpen] = useState(false);

  // Derive processed/patch statuses for status cards
  const partnerInfo = {
    partnerProcessedStatus: eachStepSummary?.VMS?.['CREATE-SYNC'],
    partnerPatchStatus: eachStepSummary?.VMS?.['UPDATE-SYNC'],
  };
  const clientInfo = {
    clientProcessedStatus: eachStepSummary?.CLIENTS?.['GET-SYNC'],
    clientPatchStatus: eachStepSummary?.CLIENTS?.['UPDATE-SYNC'],
  };

  // ── Content summary counts ───────────────────────────────────────────────
  const logCount = activityLog?.length ?? 0;
  const hasPartner = isHavingPartnerFlow && !_.isNil(partner);
  const hasClient = isHavingClientFlow && !_.isNil(client);
  const totalCount = logCount + (hasPartner ? 1 : 0) + (hasClient ? 1 : 0);
  const hasContent = totalCount > 0;

  return (
    <>
      {/* ── Disclosure button ─────────────────────────────────────── */}
      <DisclosureButton
        open={open}
        onClick={() => setOpen((p) => !p)}
        hasContent={hasContent}
        count={totalCount}
        testId={toggleTestId}
      />

      {/* ── Expanded content ─────────────────────────────────────── */}
      {open && (
        <div
          data-testid={contentTestId ?? ''}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 items-stretch"
        >
          {/* Activity Timeline */}
          {isActivityLogDataReceived ? (
            <SectionCard
              title={SectionTitles.ACTIVITY_TIMELINE}
              icon={<FiActivity className="h-3.5 w-3.5" />}
              badge={activityLog?.length}
            >
              <div className="max-h-80 overflow-y-auto scrollbar-thin px-4 py-3">
                {activityLog?.map((event, idx) => (
                  <AlertTimelineItem key={idx} event={event} />
                ))}
              </div>
            </SectionCard>
          ) : (
            <SectionCard
              title={SectionTitles.ACTIVITY_TIMELINE}
              icon={<FiActivity className="h-3.5 w-3.5" />}
            >
              <NoDataPlate
                message={NoDataMessages.NO_ACTIVITY_LOG_RECEIVED}
                icon={<FiAlertCircle className="h-7 w-7" />}
              />
            </SectionCard>
          )}

          {/* Partner */}
          {isHavingPartnerFlow && !_.isNil(partner) ? (
            <AlertStatusCard
              title={CardTitles.PARTNER}
              statusData={partner!}
              partnerInfo={partnerInfo}
              clientInfo={clientInfo}
            />
          ) : (
            <SectionCard
              title={CardTitles.PARTNER}
              icon={<FiServer className="h-3.5 w-3.5 text-white" />}
              compact
            >
              <NoDataPlate
                message={ORGANISM_PROPS.ALERT_MORE_DETAILS.NO_PARTNER_DATA}
              />
            </SectionCard>
          )}

          {/* Client */}
          {isHavingClientFlow && !_.isNil(client) ? (
            <AlertStatusCard
              title={CardTitles.CLIENT}
              statusData={client!}
              partnerInfo={partnerInfo}
              clientInfo={clientInfo}
            />
          ) : (
            <SectionCard
              title={CardTitles.CLIENT}
              icon={<FiMonitor className="h-3.5 w-3.5 text-white" />}
              compact
            >
              <NoDataPlate
                message={ORGANISM_PROPS.ALERT_MORE_DETAILS.NO_CLIENT_DATA}
              />
            </SectionCard>
          )}
        </div>
      )}
    </>
  );
};
